import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { colors, fonts, spacing, borderRadius, shadows, tableStyles } from '../constants';
import api from '../services/api';
import { useSnackbar } from '../components/common/Snackbar';
import SearchAndFilter from '../components/queries/SearchAndFilter';
import { formatStatusLabel, getStatusColor } from '../utils/statusUtils';
import { formatDateTime } from '../utils/dateUtils';

const ClosedQueries = () => {
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    projectName: '',
  });

  useEffect(() => {
    fetchQueries();
  }, [page, rowsPerPage, searchQuery, filters]);

  const fetchQueries = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        isClosed: true,
      };
      
      // Add search parameter if present
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Add filters
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.projectName) params.projectName = filters.projectName;

      const response = await api.get('/queries', { params });
      let fetched = response.data.data.queries || [];
      // Client-side fallback search
      const trimmedSearch = (searchQuery || '').trim().toLowerCase();
      if (trimmedSearch) {
        fetched = fetched.filter((q) => {
          const id = (q.queryId || q._id || '').toString().toLowerCase();
          const header = (q.queryHeader || '').toLowerCase();
          const desc = (q.queryDescription || '').toLowerCase();
          const project = (q.projectName || '').toLowerCase();
          return (
            id.includes(trimmedSearch) ||
            header.includes(trimmedSearch) ||
            desc.includes(trimmedSearch) ||
            project.includes(trimmedSearch)
          );
        });
      }
      setQueries(fetched);
      setTotal(trimmedSearch ? fetched.length : response.data.data.pagination.total);
    } catch (error) {
      // console.error('Error fetching queries:', error);
      showSnackbar('Failed to load queries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
      case 'urgent':
        return colors.error;
      case 'medium':
        return colors.warning;
      case 'low':
        return colors.info;
      default:
        return colors.textSecondary;
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset to page 0 when search or filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, filters]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background, p: spacing.md }}>
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        <Typography variant="body1" sx={{ color: colors.textSecondary, fontSize: fonts.fontSizeMd, mb: spacing.md, mt: 0 }}>
          View all closed and resolved queries.
        </Typography>

        <SearchAndFilter
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          searchValue={searchQuery}
          filters={filters}
          showStatusFilter={true}
          showPriorityFilter={true}
          showProjectFilter={true}
          placeholder="Search closed queries by ID, title, description, or project..."
        />

        <Paper sx={{ borderRadius: borderRadius.md, boxShadow: shadows.md }}>
          <Box sx={{ p: spacing.sm, borderBottom: `1px solid ${colors.borderDefault}` }}>
            <Typography variant="h6" sx={{ fontWeight: fonts.weightBold, fontSize: fonts.fontSizeMd }}>
              Closed Queries {total > 0 && `(${total})`}
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.backgroundSoft }}>
                  <TableCell sx={tableStyles.header}>Query ID</TableCell>
                  {/* <TableCell sx={tableStyles.header}>Title</TableCell> */}
                  <TableCell sx={tableStyles.header}>Status</TableCell>
                  <TableCell sx={tableStyles.header}>Priority</TableCell>
                  <TableCell sx={tableStyles.header}>Raised By</TableCell>
                  <TableCell sx={tableStyles.header}>Assigned To</TableCell>
                  <TableCell sx={tableStyles.header}>Created Date</TableCell>
                  <TableCell sx={tableStyles.header}>Closed Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.xl }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : queries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.xl }}>
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        No queries found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  queries.map((query) => (
                    <TableRow key={query._id} hover sx={{ '&:hover': { backgroundColor: colors.backgroundLight } }}>
                      <TableCell sx={{ ...tableStyles.cell }}>{query.queryId || query._id}</TableCell>
                      {/* <TableCell sx={{ ...tableStyles.cell, fontWeight: fonts.weightMedium }}>{query.queryHeader}</TableCell> */}
                      <TableCell sx={{ ...tableStyles.cell }}>
                        <Chip
                          label={formatStatusLabel(query.status, { uppercase: true })}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(query.status) + '20',
                            color: getStatusColor(query.status),
                            fontWeight: fonts.weightSemiBold,
                            fontSize: fonts.fontSizeXs,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        <Chip
                          label={query.priority?.toUpperCase() || 'N/A'}
                          size="small"
                          sx={{
                            backgroundColor: getPriorityColor(query.priority) + '20',
                            color: getPriorityColor(query.priority),
                            fontWeight: fonts.weightSemiBold,
                            fontSize: fonts.fontSizeXs,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>{query.raisedBy?.name || 'N/A'}</TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>{query.assignee?.name || 'N/A'}</TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>{formatDateTime(query.raisedAt)}</TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>{formatDateTime(query.closedAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: `1px solid ${colors.borderDefault}`,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: fonts.fontSizeXs,
                color: colors.textSecondary,
              },
              '& .MuiIconButton-root': {
                color: colors.textSecondary,
                '&:hover': {
                  backgroundColor: colors.backgroundSoft,
                },
              },
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default ClosedQueries;

